import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router';
import { motion, useReducedMotion } from 'motion/react';
import {
  ArrowRight,
  Code2,
  GitBranch,
  Lock,
  Menu as MenuIcon,
  MessageSquareText,
  MousePointerClick,
  Presentation,
  Server,
  Users,
  WifiOff,
  X,
} from 'lucide-react';
import { BUILTIN_TEMPLATES, stickyColor } from '@lumo/shared';
import { Logo } from '@/components/Logo';
import { ThemeSwitcher } from '@/components/ThemeSwitcher';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { TemplatePreview } from '@/dashboard/TemplatePreview';
import { useMe } from '@/lib/auth';
import { cn } from '@/lib/cn';
import { useTheme } from '@/lib/theme';

import { tr } from '@/i18n';
const DemoBoard = lazy(() => import('./landing/DemoBoard'));

// ─── Мелкие детали в стиле доски ─────────────────────────────────────

/** Кнопка-пилюля: как кнопки на панелях доски, только круглая. */
function Pill({ to, onClick, variant = 'primary', size = 'md', children, className }: { to?: string; onClick?: () => void; variant?: 'primary' | 'glass' | 'ghost'; size?: 'sm' | 'md' | 'lg'; children: ReactNode; className?: string }) {
  const cls = cn(
    'inline-flex items-center justify-center gap-2 rounded-full font-medium whitespace-nowrap transition-[transform,background-color,box-shadow] active:scale-[0.98] [&_svg]:size-4',
    size === 'sm' && 'h-9 px-4 text-[13px]',
    size === 'md' && 'h-10 px-5 text-sm',
    size === 'lg' && 'h-12 px-6 text-[15px]',
    variant === 'primary' && 'bg-accent text-white shadow-[0_1px_2px_rgb(0_0_0/0.12),inset_0_1px_0_rgb(255_255_255/0.16)] hover:bg-accent-700',
    variant === 'glass' && 'glass !rounded-full text-fg hover:-translate-y-px',
    variant === 'ghost' && 'text-fg-muted hover:bg-hover hover:text-fg',
    className,
  );
  if (to) {
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={cls}>
      {children}
    </button>
  );
}

/** Подпись над блоком — как заголовок фрейма на доске. */
function FrameLabel({ children }: { children: ReactNode }) {
  return <div className="mb-2 pl-1 text-[13px] font-medium text-fg-muted">{children}</div>;
}

/** Стикер-декор: его можно потаскать. */
function Sticky({ color, text, className, rotate }: { color: string; text: string; className?: string; rotate: number }) {
  const theme = useTheme((s) => s.resolved);
  const reduce = useReducedMotion();
  return (
    <motion.div
      drag={!reduce}
      dragMomentum={false}
      whileDrag={{ scale: 1.06, rotate: 0, zIndex: 10, cursor: 'grabbing' }}
      initial={reduce ? false : { opacity: 0, scale: 0.8, rotate: rotate * 2 }}
      animate={{ opacity: 1, scale: 1, rotate }}
      transition={{ type: 'spring', stiffness: 260, damping: 20, delay: 0.3 }}
      className={cn(
        'absolute hidden size-32 cursor-grab touch-none items-center justify-center rounded-[3px] p-3 text-center font-[Caveat] text-[22px] leading-tight text-stone-900 shadow-[0_1px_1px_rgb(0_0_0/0.06),0_10px_20px_-8px_rgb(0_0_0/0.25)] select-none lg:flex',
        className,
      )}
      style={{ background: stickyColor(color, theme) }}
      aria-hidden
    >
      {text}
    </motion.div>
  );
}

const PEERS = [
  { name: tr("Маша"), color: '#E5484D', path: { left: ['18%', '34%', '30%', '22%', '18%'], top: ['30%', '22%', '48%', '56%', '30%'] }, duration: 11 },
  { name: tr("Иван"), color: '#0090FF', path: { left: ['70%', '62%', '78%', '84%', '70%'], top: ['62%', '40%', '30%', '58%', '62%'] }, duration: 13 },
  { name: tr("Лена"), color: '#30A46C', path: { left: ['48%', '56%', '44%', '40%', '48%'], top: ['74%', '66%', '60%', '78%', '74%'] }, duration: 9 },
];

function PeerCursors() {
  const reduce = useReducedMotion();
  if (reduce) return null;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
      {PEERS.map((p) => (
        <motion.div
          key={p.name}
          className="absolute"
          initial={{ left: p.path.left[0], top: p.path.top[0] }}
          animate={{ left: p.path.left, top: p.path.top }}
          transition={{ duration: p.duration, repeat: Infinity, ease: 'easeInOut' }}
        >
          <svg width="18" height="18" viewBox="0 0 18 18" className="drop-shadow-sm">
            <path d="M2 1.5 L15.5 8.2 L9.2 9.6 L6.4 15.8 Z" fill={p.color} stroke="#fff" strokeWidth="1.4" strokeLinejoin="round" />
          </svg>
          <span className="ml-3 inline-block rounded-full px-2 py-0.5 text-[11px] font-medium text-white shadow-sm" style={{ background: p.color }}>
            {p.name}
          </span>
        </motion.div>
      ))}
    </div>
  );
}

function Reveal({ children, delay = 0, className }: { children: ReactNode; delay?: number; className?: string }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      className={className}
      initial={reduce ? false : { opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-60px' }}
      transition={{ duration: 0.45, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function Section({ id, eyebrow, title, text, children }: { id?: string; eyebrow: string; title: string; text?: string; children: ReactNode }) {
  return (
    <section id={id} className="mx-auto max-w-6xl scroll-mt-28 px-4 py-16 sm:px-6 sm:py-20">
      <Reveal className="max-w-2xl">
        <span className="glass inline-flex !rounded-full px-3 py-1 text-[12px] font-semibold text-accent-fg">{eyebrow}</span>
        <h2 className="mt-4 text-3xl font-semibold tracking-tight text-balance sm:text-4xl">{title}</h2>
        {text && <p className="mt-3 text-[17px] text-pretty text-fg-muted">{text}</p>}
      </Reveal>
      <div className="mt-10">{children}</div>
    </section>
  );
}

// ─── Шапка-пилюля ────────────────────────────────────────────────────

const NAV = [
  { href: '#features', label: tr("Возможности") },
  { href: '#templates', label: tr("Шаблоны") },
  { href: '#self-hosted', label: tr("Свой сервер") },
  { to: '/explore', label: tr("Галерея") },
  { to: '/developers', label: 'API' },
];

function Header({ loggedIn }: { loggedIn: boolean }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener('scroll', on, { passive: true });
    return () => window.removeEventListener('scroll', on);
  }, []);
  const link = 'inline-flex h-9 items-center rounded-full px-3.5 text-[13.5px] font-medium text-fg-muted transition-colors hover:bg-hover hover:text-fg';
  return (
    <div className="pointer-events-none fixed inset-x-0 top-3 z-40 flex justify-center px-3">
      <header className={cn('glass pointer-events-auto flex w-full max-w-5xl flex-col !rounded-[28px] transition-shadow', scrolled && 'shadow-float')}>
        <div className="flex h-14 items-center gap-1 pr-2 pl-4">
          <Link to="/" aria-label={tr("Lumo — на главную")} className="mr-3 flex items-center rounded-full">
            <Logo />
          </Link>
          <nav className="hidden items-center md:flex" aria-label={tr("Разделы")}>
            {NAV.map((n) =>
              n.to ? (
                <Link key={n.label} to={n.to} className={link}>
                  {n.label}
                </Link>
              ) : (
                <a key={n.label} href={n.href} className={link}>
                  {n.label}
                </a>
              ),
            )}
          </nav>
          <div className="ml-auto flex items-center gap-1.5">
            <LanguageSwitcher className="hidden !rounded-full sm:inline-flex [&_button]:!rounded-full" />
            <ThemeSwitcher className="hidden !rounded-full sm:inline-flex [&_button]:!rounded-full" />
            {loggedIn ? (
              <Pill to="/dashboard" size="sm">
                {tr("Мои доски")}
              </Pill>
            ) : (
              <>
                <Pill to="/login" variant="ghost" size="sm" className="hidden sm:inline-flex">
                  {tr("Войти")}
                </Pill>
                <Pill to="/register" size="sm">
                  {tr("Начать")}
                </Pill>
              </>
            )}
            <button
              type="button"
              aria-label={open ? tr("Закрыть меню") : tr("Меню")}
              aria-expanded={open}
              onClick={() => setOpen((v) => !v)}
              className="inline-flex size-9 items-center justify-center rounded-full text-fg-muted hover:bg-hover hover:text-fg md:hidden [&_svg]:size-[18px]"
            >
              {open ? <X /> : <MenuIcon />}
            </button>
          </div>
        </div>
        {open && (
          <nav className="flex flex-col gap-0.5 border-t border-border/60 p-2 md:hidden" aria-label={tr("Разделы")}>
            {NAV.map((n) =>
              n.to ? (
                <Link key={n.label} to={n.to} className={cn(link, 'h-10 justify-start')} onClick={() => setOpen(false)}>
                  {n.label}
                </Link>
              ) : (
                <a key={n.label} href={n.href} className={cn(link, 'h-10 justify-start')} onClick={() => setOpen(false)}>
                  {n.label}
                </a>
              ),
            )}
            {!loggedIn && (
              <Link to="/login" className={cn(link, 'h-10 justify-start')}>
                {tr("Войти")}
              </Link>
            )}
            <div className="mt-1 flex gap-2">
              <ThemeSwitcher className="!rounded-full [&_button]:!rounded-full" />
              <LanguageSwitcher className="!rounded-full [&_button]:!rounded-full" />
            </div>
          </nav>
        )}
      </header>
    </div>
  );
}

// ─── Страница ────────────────────────────────────────────────────────

const FEATURES: Array<{ icon: typeof Users; color: string; title: string; text: string }> = [
  { icon: Users, color: 'lavender', title: tr("Вместе в реальном времени"), text: tr("Курсоры, выделения и правки коллег видны сразу. Следуйте за ведущим или позовите всех к себе.") },
  { icon: WifiOff, color: 'sky', title: tr("Работает без сети"), text: tr("Изменения сохраняются в браузере и сливаются без конфликтов, когда связь вернётся.") },
  { icon: MousePointerClick, color: 'yellow', title: tr("Всё для мышления"), text: tr("Стикеры, фигуры, коннекторы с обходом препятствий, перо, фреймы, mind map, таблицы и карточки.") },
  { icon: Presentation, color: 'peach', title: tr("Встречи на доске"), text: tr("Голосование точками, таймер, режим презентации по фреймам и лазерная указка.") },
  { icon: MessageSquareText, color: 'mint', title: tr("Комментарии"), text: tr("Треды прямо на объектах, упоминания и уведомления — обсуждение не теряется.") },
  { icon: Code2, color: 'pink', title: tr("API и вебхуки"), text: tr("REST API с токенами и подписанные вебхуки — подключайте доски к своим процессам.") },
];

export function HomePage() {
  const me = useMe();
  const navigate = useNavigate();
  const theme = useTheme((s) => s.resolved);
  const loggedIn = !!me.data?.user;
  const templates = useMemo(() => BUILTIN_TEMPLATES.filter((t) => t.id !== 'builtin:blank-grid').slice(0, 6).map((t) => ({ tpl: t, content: t.build() })), []);
  const start = loggedIn ? '/dashboard' : '/register';

  return (
    <div className="canvas-dots relative min-h-full overflow-x-clip text-fg">
      <Header loggedIn={loggedIn} />

      {/* Hero */}
      <section id="main" tabIndex={-1} className="relative outline-none">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-[560px] bg-[radial-gradient(55%_60%_at_50%_0%,color-mix(in_oklch,var(--color-accent)_13%,transparent),transparent)]" aria-hidden />
        <div className="relative mx-auto max-w-6xl px-4 pt-32 pb-12 text-center sm:px-6 sm:pt-40">
          <Sticky color="yellow" text={tr("Идеи на пятницу")} rotate={-6} className="top-36 left-6" />
          <Sticky color="pink" text={tr("Ретро в 16:00 ✦")} rotate={5} className="top-72 left-24" />
          <Sticky color="mint" text={tr("Релиз готов!")} rotate={4} className="top-40 right-10" />
          <Sticky color="sky" text={tr("Вопросы к дизайну?")} rotate={-4} className="top-80 right-28" />

          <Reveal>
            <span className="glass inline-flex items-center gap-2 !rounded-full px-3.5 py-1.5 text-[13px] text-fg-muted">
              <span className="size-1.5 rounded-full bg-success" /> {tr("Открытый код · свой сервер · без лимитов")}
            </span>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="mx-auto mt-6 max-w-3xl text-4xl font-semibold tracking-tight text-balance sm:text-6xl">
              {tr("Думайте вместе на")}{' '}
              <span className="relative whitespace-nowrap text-accent-fg">
                {tr("бесконечной")}
                <svg viewBox="0 0 300 16" preserveAspectRatio="none" className="absolute -bottom-2 left-0 h-3 w-full text-accent/50" aria-hidden>
                  <path d="M3 11 C 70 3, 150 3, 297 9" fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" />
                </svg>
              </span>{' '}
              {tr("доске")}
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mx-auto mt-6 max-w-2xl text-[17px] text-pretty text-fg-muted sm:text-lg">
              {tr("Lumo — онлайн-доска для мозговых штурмов, ретро, схем и планирования. Работает в браузере и на планшете, а данные остаются на вашем сервере.")}
            </p>
          </Reveal>
          <Reveal delay={0.15} className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Pill to={start} size="lg">
              {loggedIn ? tr("Открыть мои доски") : tr("Начать бесплатно")} <ArrowRight />
            </Pill>
            <Pill to="/local/sandbox" variant="glass" size="lg">
              {tr("Попробовать без регистрации")}
            </Pill>
          </Reveal>
        </div>

        {/* Живая доска — как фрейм на холсте */}
        <Reveal delay={0.2} className="relative mx-auto max-w-6xl px-4 pb-4 sm:px-6">
          <FrameLabel>{tr("Демо-доска")}</FrameLabel>
          <div className="relative h-[440px] overflow-hidden rounded-[20px] bg-canvas shadow-float ring-1 ring-border sm:h-[560px]">
            <Suspense fallback={<div className="canvas-dots absolute inset-0" />}>
              <DemoBoard />
            </Suspense>
            <PeerCursors />
            <div className="glass pointer-events-none absolute top-3 left-3 flex items-center gap-2 !rounded-full px-3 py-1.5 text-[12px] text-fg-muted">
              <span className="size-1.5 animate-pulse rounded-full bg-success" /> {tr("Живая доска")}<span className="hidden sm:inline"> {tr("— перетаскивайте, рисуйте, двойной клик для текста")}</span>
            </div>
            <div className="glass pointer-events-none absolute top-3 right-3 hidden items-center -space-x-1.5 !rounded-full p-1.5 sm:flex">
              {PEERS.map((p) => (
                <span key={p.name} className="flex size-7 items-center justify-center rounded-full text-[11px] font-semibold text-white ring-2 ring-[var(--lumo-panel)]" style={{ background: p.color }}>
                  {p.name[0]}
                </span>
              ))}
            </div>
          </div>
          <p className="mt-3 text-center text-[13px] text-fg-subtle">{tr("Масштаб — Ctrl/⌘ + колесо или жест на тачпаде. Всё, что вы нарисуете здесь, останется только в вашем браузере.")}</p>
        </Reveal>
      </section>

      {/* Возможности */}
      <Section id="features" eyebrow={tr("Возможности")} title={tr("Всё, что нужно команде, — на одном холсте")} text={tr("Без платных тарифов и урезанных функций: всё доступно сразу.")}>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {FEATURES.map((f, i) => (
            <Reveal key={f.title} delay={(i % 3) * 0.05} className="h-full">
              <div className="glass h-full p-6 transition-transform duration-200 hover:-translate-y-0.5">
                <span className="flex size-11 items-center justify-center rounded-[4px] text-stone-900 shadow-[0_1px_1px_rgb(0_0_0/0.06),0_6px_12px_-6px_rgb(0_0_0/0.25)] [&_svg]:size-5" style={{ background: stickyColor(f.color, theme), rotate: `${((i % 3) - 1) * 3}deg` }}>
                  <f.icon />
                </span>
                <h3 className="mt-4 text-[16px] font-semibold">{f.title}</h3>
                <p className="mt-1.5 text-[14px] leading-relaxed text-fg-muted">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Шаблоны — как фреймы на доске */}
      <Section id="templates" eyebrow={tr("Шаблоны")} title={tr("Начните не с чистого листа")} text={tr("Ретроспективы, канбан, story map, mind map, SWOT, customer journey — и свои шаблоны команды.")}>
        <div className="grid gap-x-4 gap-y-6 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map(({ tpl, content }, i) => (
            <Reveal key={tpl.id} delay={(i % 3) * 0.05}>
              <Link to={loggedIn ? '/dashboard/templates' : '/register'} className="group block rounded-[18px] focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none">
                <FrameLabel>{tr(tpl.name)}</FrameLabel>
                <div className="overflow-hidden rounded-[14px] bg-surface shadow-card ring-1 ring-border transition-shadow group-hover:shadow-float">
                  <TemplatePreview content={content} className="aspect-[16/10] w-full transition-transform duration-300 group-hover:scale-[1.03]" />
                </div>
                <p className="mt-2 line-clamp-2 pl-1 text-[13px] text-fg-muted">{tr(tpl.description)}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* Свой сервер */}
      <Section id="self-hosted" eyebrow={tr("Свой сервер")} title={tr("Ваши доски — на вашем сервере")} text={tr("Один Node.js-процесс на сервис, SQLite вместо отдельной базы, никаких внешних облаков. Поднимается за пару минут.")}>
        <div className="grid items-start gap-6 lg:grid-cols-[1.2fr_1fr]">
          <Reveal>
            <div className="overflow-hidden rounded-[20px] bg-[#1C1917] text-[13.5px] text-stone-200 shadow-float ring-1 ring-white/10">
              <div className="flex items-center gap-1.5 border-b border-white/10 px-4 py-3">
                <span className="size-2.5 rounded-full bg-[#FF5F57]" />
                <span className="size-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="size-2.5 rounded-full bg-[#28C840]" />
                <span className="ml-2 text-[12px] text-stone-400">terminal</span>
              </div>
              <pre className="overflow-x-auto p-5 font-mono leading-relaxed">
                <span className="text-stone-500">{tr("# зависимости и сборка")}</span>
                {'\n'}pnpm i && pnpm build
                {'\n\n'}
                <span className="text-stone-500">{tr("# миграции и демо-данные")}</span>
                {'\n'}pnpm db:migrate && pnpm seed
                {'\n\n'}
                <span className="text-stone-500">{tr("# запуск под PM2")}</span>
                {'\n'}pm2 start ecosystem.config.cjs
              </pre>
            </div>
          </Reveal>
          <Reveal delay={0.05}>
            <ul className="glass flex flex-col gap-5 p-6">
              {[
                { icon: Server, title: tr("Без Docker и облаков"), text: tr("PM2, SQLite в режиме WAL и папка с файлами. Бэкап — одной командой.") },
                { icon: Lock, title: tr("Доступ под контролем"), text: tr("Роли в воркспейсах и досках, ссылки с паролем и сроком, публичные доски по желанию.") },
                { icon: GitBranch, title: tr("Открытый код"), text: tr("Только свободные зависимости (MIT, Apache, BSD). Никакой телеметрии.") },
              ].map((x) => (
                <li key={x.title} className="flex gap-3">
                  <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-accent-fg [&_svg]:size-[18px]">
                    <x.icon />
                  </span>
                  <div>
                    <div className="font-medium">{x.title}</div>
                    <div className="text-[14px] text-fg-muted">{x.text}</div>
                  </div>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </Section>

      {/* Призыв */}
      <section className="mx-auto max-w-6xl px-4 pb-10 sm:px-6">
        <Reveal>
          <div className="relative overflow-hidden rounded-[28px] bg-accent px-6 py-14 text-center text-white sm:px-12">
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(rgb(255_255_255/0.2)_1px,transparent_1.2px)] bg-[size:24px_24px]" aria-hidden />
            <h2 className="relative text-3xl font-semibold tracking-tight text-balance sm:text-4xl">{tr("Соберите команду на одной доске")}</h2>
            <p className="relative mx-auto mt-3 max-w-xl text-white/80">{tr("Создайте доску за секунды и пригласите коллег ссылкой.")}</p>
            <div className="relative mt-7 flex flex-wrap justify-center gap-3">
              <button type="button" onClick={() => navigate(start)} className="inline-flex h-12 items-center gap-2 rounded-full bg-white px-6 text-[15px] font-medium text-accent-fg shadow-sm transition-transform hover:-translate-y-px">
                {loggedIn ? tr("Открыть мои доски") : tr("Создать аккаунт")} <ArrowRight className="size-4" />
              </button>
              <Link to="/explore" className="inline-flex h-12 items-center rounded-full px-6 text-[15px] font-medium text-white ring-1 ring-white/40 hover:bg-white/10">
                {tr("Смотреть публичные доски")}
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Подвал — тоже пилюля */}
      <footer className="mx-auto max-w-6xl px-4 pb-6 sm:px-6">
        <div className="glass flex flex-col gap-3 !rounded-[28px] px-5 py-4 text-[13px] text-fg-muted sm:flex-row sm:items-center sm:!rounded-full">
          <Logo className="[&_svg]:size-6" />
          <span>{tr("Онлайн-доска для совместной работы")}</span>
          <nav className="flex flex-wrap gap-1 sm:ml-auto" aria-label={tr("Ссылки")}>
            {[
              ['/explore', tr("Галерея")],
              ['/developers', 'API'],
              ['/local/sandbox', tr("Песочница")],
              ['/login', tr("Вход")],
            ].map(([to, label]) => (
              <Link key={to} to={to!} className="rounded-full px-3 py-1.5 hover:bg-hover hover:text-fg">
                {label}
              </Link>
            ))}
          </nav>
        </div>
        {/* Бейдж студии — по правилам айдентики LKXA: ≤32 px, ссылка с ref=badge, вариант под тему. */}
        <div className="mt-4 flex justify-center">
          <a
            href="https://lkxa.ru/?ref=badge"
            target="_blank"
            rel="noopener"
            className="rounded-full transition-opacity hover:opacity-80 focus-visible:ring-2 focus-visible:ring-accent focus-visible:outline-none"
          >
            <img src="/brand/long-light.svg" alt="Designed & developed by LKXA" height={32} className="block h-8 w-auto dark:hidden" />
            <img src="/brand/long-dark.svg" alt="Designed & developed by LKXA" height={32} className="hidden h-8 w-auto dark:block" />
          </a>
        </div>
      </footer>
    </div>
  );
}
